import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { AuthGuardService } from '@features/auth/auth-guard.service';
import { UserService } from '@features/auth/user.service';
import { TermsOfUseAgreementRepository } from '@features/terms-of-use/terms-of-use-agreement-repository';
import { combineLatest, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

@Injectable()
export class TermsOfUseGuard implements CanActivate {
  private static readonly termsLastChanged: Date = new Date('2018-04-26');

  constructor(
    private router: Router,
    private termsOfUseAgreementRepository: TermsOfUseAgreementRepository,
    private userService: UserService,
    private authGuard: AuthGuardService,
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> {
    return this.authGuard.canActivate(route, state).pipe(
      switchMap(() => combineLatest(this.userService.user$, this.hasAgreedToTermsOfUse()).pipe(
        map(([user, hasAgreed]) => {
          if (!user.isExternalUser || hasAgreed) {
            return true;
          }

          return this.router.createUrlTree(['terms-of-use'], {
            queryParams: { redirectTo: state.url },
          });
        }),
      )),
    );
  }

  private hasAgreedToTermsOfUse(): Observable<boolean> {
    const lastChanged = TermsOfUseGuard.termsLastChanged;
    const agreements$ = this.termsOfUseAgreementRepository.findAfter(lastChanged);

    return agreements$.pipe(map((agreements) => agreements.length > 0));
  }
}
